import {
  useState,
  useRef,
  useEffect,
  type ElementType,
  type ReactNode,
  type KeyboardEvent,
  type MouseEvent,
} from 'react';
import { Check, X } from 'lucide-react';
import { useDynamicText } from '@/lib/dynamic-text-context';
import { resolveCopyValue } from '@/lib/dynamic-text-utils';
import initialCopyData from '@/data/copy.json';

export interface DynamicTextProps {
  /**
   * Dot-notated path to the copy key (e.g. "hero.title" or "text.hero.title").
   */
  path: string;
  /**
   * Fallback text if the key is not present in copy.json.
   * In development, this default will also be auto-initialized into copy.json.
   */
  children?: ReactNode;
  /**
   * The HTML element or React component to render. Defaults to 'span'.
   */
  as?: ElementType;
  /**
   * Optional CSS classes passed to the rendered element.
   */
  className?: string;
}

function DevDynamicText({
  path,
  children,
  as: Component = 'span',
  className,
}: DynamicTextProps) {
  const { getText, updateText, initText } = useDynamicText();
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const editableRef = useRef<HTMLSpanElement>(null);

  const defaultText = typeof children === 'string' || typeof children === 'number' ? String(children) : '';
  const storedValue = getText(path);
  const hasStored = storedValue !== undefined && storedValue !== '';
  const displayText = hasStored ? storedValue : (defaultText || `[${path}]`);

  // Auto-initialize missing keys in dev when children text is supplied
  useEffect(() => {
    if (!hasStored && defaultText) {
      void initText(path, defaultText);
    }
  }, [hasStored, defaultText, path, initText]);

  // Focus and select text when entering edit mode
  useEffect(() => {
    if (isEditing && editableRef.current) {
      editableRef.current.focus();
      try {
        const range = document.createRange();
        range.selectNodeContents(editableRef.current);
        const sel = window.getSelection();
        sel?.removeAllRanges();
        sel?.addRange(range);
      } catch {
        // Selection range can fail silently in certain test environments
      }
    }
  }, [isEditing]);

  const handleStartEdit = (e: MouseEvent) => {
    if (e.altKey) {
      e.preventDefault();
      e.stopPropagation();
      setIsEditing(true);
    }
  };

  const handleConfirm = async (e?: MouseEvent | KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const newText = editableRef.current?.innerText ?? displayText;
    setIsSaving(true);
    try {
      await updateText(path, newText);
      setIsEditing(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = (e?: MouseEvent | KeyboardEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (editableRef.current) {
      editableRef.current.innerText = displayText;
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLSpanElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleConfirm(e);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel(e);
    }
  };

  if (isEditing) {
    return (
      <Component
        className={`relative inline-flex flex-wrap items-center gap-1.5 rounded-sm outline outline-2 outline-amber-500/80 bg-background/80 px-1 py-0.5 z-50 ${className ?? ''}`}
      >
        <span
          ref={editableRef}
          contentEditable
          suppressContentEditableWarning
          onKeyDown={handleKeyDown}
          className="outline-none min-w-[1ch] text-inherit font-inherit leading-inherit"
          data-testid="dynamic-text-editable"
        >
          {displayText}
        </span>
        <span
          className="inline-flex items-center gap-1 shrink-0 select-none align-middle not-italic text-sm font-sans"
          contentEditable={false}
        >
          <button
            type="button"
            data-testid="dynamic-text-confirm"
            aria-label="Confirm edit"
            title="Confirm (Enter)"
            disabled={isSaving}
            onClick={handleConfirm}
            className="inline-flex size-5 items-center justify-center rounded bg-emerald-600 hover:bg-emerald-500 text-white shadow-sm transition-transform active:scale-95 disabled:opacity-50"
          >
            <Check className="size-3.5 stroke-[2.5]" />
          </button>
          <button
            type="button"
            data-testid="dynamic-text-cancel"
            aria-label="Cancel edit"
            title="Cancel (Esc)"
            disabled={isSaving}
            onClick={handleCancel}
            className="inline-flex size-5 items-center justify-center rounded bg-zinc-600 hover:bg-zinc-500 text-white shadow-sm transition-transform active:scale-95 disabled:opacity-50"
          >
            <X className="size-3.5 stroke-[2.5]" />
          </button>
        </span>
      </Component>
    );
  }

  return (
    <Component
      onClick={handleStartEdit}
      title={`Alt + click to edit (${path})`}
      data-path={path}
      className={`transition-[outline,box-shadow] hover:outline hover:outline-1 hover:outline-dashed hover:outline-amber-500/60 hover:rounded-sm ${className ?? ''}`}
    >
      {displayText}
    </Component>
  );
}

export function DynamicText({
  path,
  children,
  as: Component = 'span',
  className,
}: DynamicTextProps) {
  if (!import.meta.env.DEV) {
    const rawVal = resolveCopyValue(initialCopyData, path);
    const defaultText = typeof children === 'string' || typeof children === 'number' ? String(children) : '';
    const text = (rawVal !== undefined && rawVal !== '') ? rawVal : (defaultText || `[${path}]`);
    return <Component className={className}>{text}</Component>;
  }

  return (
    <DevDynamicText path={path} as={Component} className={className}>
      {children}
    </DevDynamicText>
  );
}
